import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useReducedMotion } from 'framer-motion';
import { useCoachMark } from './CoachMarkProvider';
import { CoachMarkCard } from './CoachMarkCard';
import { placeCard, CoachRect, CoachSize, PlacedCard, COACH_RING_Z } from '../../lib/coachPlacement';
import { attachAdvanceListener } from '../../lib/coachAdvance';
import type { CoachAdvance } from '../../lib/coachTypes';
import { countedNumber, countedSlots, currentStep, slotsOf } from '../../lib/coachRun';
import { blockingDialogElement } from '../../lib/coachDefer';
import { CoachState, clipRectToViewport, isTallAnchor, readCoachState, sameCoachState } from '../../lib/coachDom';

interface RingBox {
  top: number;
  left: number;
  width: number;
  height: number;
  radius: string;
}

const DEFAULT_CARD_SIZE: CoachSize = { width: 300, height: 160 };
/** How long an optional card waits for its target after the previous card
 *  was advanced by an interaction (a click that opens a modal needs a render
 *  or two). Reached by Next/Back instead, an absent target skips at once. */
const OPTIONAL_REVEAL_GRACE_MS = 700;

/** The line under an advance step when the author did not write one. */
function defaultHint(advance: CoachAdvance | undefined): string | undefined {
  if (!advance) return undefined;
  if (advance.kind === 'click') return 'Try it: {click} here';
  if (advance.kind === 'input') return 'Try it: type here';
  return undefined;
}

/**
 * CoachMarkHost — anchor resolution, the measure loop, the portal, the ring,
 * Escape, and (v2) the interaction listeners that let a step advance when
 * the person does the thing it describes. Mounted once, as a sibling of
 * AppContent (App.tsx), so it survives every route change the way the tour
 * itself needs to.
 *
 * Two deliberate departures from AppCardMenu.tsx (our positioning
 * reference): we do NOT close on scroll (we follow — see the measure loop
 * below) and we do NOT close on outside click — the tour describes live UI
 * and the user must be able to click it. Only X, Escape, and finishing end
 * a run.
 */
export const CoachMarkHost: React.FC = () => {
  const { run, paused, next, back, end } = useCoachMark();
  const reducedMotion = useReducedMotion();

  const cardRef = useRef<HTMLDivElement | null>(null);
  const anchorElRef = useRef<Element | null>(null);
  const anchorResizeObserverRef = useRef<ResizeObserver | null>(null);
  const scrolledForStepRef = useRef<string | null>(null);
  const rafRef = useRef<number | null>(null);
  const settleRafRef = useRef<number | null>(null);
  // Interaction-aware advance: one live listener at a time, re-attached
  // whenever the step OR the anchor element changes (a sidebar collapse
  // swaps the subtree, so the same step can land on a new element).
  const advanceCleanupRef = useRef<(() => void) | null>(null);
  const advanceStepKeyRef = useRef<string | null>(null);
  const onAdvanceRef = useRef<() => void>(() => {});

  const [cardSize, setCardSize] = useState<CoachSize>(DEFAULT_CARD_SIZE);
  const [placed, setPlaced] = useState<PlacedCard | null>(null);
  const [ring, setRing] = useState<RingBox | null>(null);
  const [centered, setCentered] = useState(false);

  // Page state (`data-coach-state`) picks which VARIANT of a card shows;
  // re-read on every measure, re-rendered only when it actually changed.
  const [coachState, setCoachState] = useState<CoachState>({});
  const coachStateRef = useRef<CoachState>({});
  // Optional cards: which way we are moving (so a skip continues in the same
  // direction), when the current slot was entered, and whether we got here
  // through an interaction (grace period for a target that is still mounting).
  const dirRef = useRef<1 | -1>(1);
  const enteredAtRef = useRef(0);
  const enteredViaAdvanceRef = useRef(false);
  const skipIssuedForRef = useRef<string | null>(null);
  const waitSettleForRef = useRef<string | null>(null);
  const [waiting, setWaiting] = useState(false);

  const slots = run ? slotsOf(run.steps) : [];
  const slotCount = slots.length;
  const step = run ? currentStep(run.steps, run.index, coachState) : null;
  const isLastStep = !!run && run.index === slotCount - 1;
  const isFirstStep = !!run && run.index === 0;

  const goNext = useCallback(() => {
    dirRef.current = 1;
    enteredViaAdvanceRef.current = false;
    enteredAtRef.current = performance.now();
    if (isLastStep) end('completed');
    else next();
  }, [isLastStep, end, next]);
  const goBack = useCallback(() => {
    dirRef.current = -1;
    enteredViaAdvanceRef.current = false;
    enteredAtRef.current = performance.now();
    back();
  }, [back]);
  // Always the latest: the listener attached inside measure() calls through
  // this ref, so it never holds a stale `isLastStep`.
  onAdvanceRef.current = () => {
    dirRef.current = 1;
    enteredViaAdvanceRef.current = true;
    // Stamped HERE, not in an effect: the first measure of the new step runs
    // in a layout effect, before any passive effect could stamp it, and an
    // old timestamp would make the grace period look already spent — the
    // optional "Use this template" card would be skipped in the same frame
    // the modal was still mounting.
    enteredAtRef.current = performance.now();
    if (isLastStep) end('completed');
    else next();
  };

  // schedule/settle are stable for the component's lifetime (empty deps) so
  // the ResizeObserver callbacks created inside measure() below never go
  // stale — they call through measureRef.current, not a closed-over
  // `measure` that might be a step behind.
  const measureRef = useRef<() => void>(() => {});

  const schedule = useCallback(() => {
    if (rafRef.current != null) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      measureRef.current();
    });
  }, []);

  // Re-measure every frame for `ms` — cheap insurance against the four
  // independent transitions that move our anchors (sidebar width, <main>
  // padding, TasksSidebar width, workspace pane mount) without firing a
  // single DOM event we could otherwise listen for.
  //
  // Single-flight: a new settle replaces the running one rather than stacking
  // beside it.
  const settle = useCallback((ms: number) => {
    if (settleRafRef.current != null) cancelAnimationFrame(settleRafRef.current);
    const deadline = performance.now() + ms;
    const tick = () => {
      measureRef.current();
      settleRafRef.current =
        performance.now() < deadline ? requestAnimationFrame(tick) : null;
    };
    tick();
  }, []);

  const detachAdvance = useCallback(() => {
    advanceCleanupRef.current?.();
    advanceCleanupRef.current = null;
    advanceStepKeyRef.current = null;
  }, []);

  // Re-run querySelector every call — never cache the anchor element. That's
  // what makes a sidebar-collapse subtree swap self-healing within a frame
  // instead of leaving the card pinned to a detached node.
  const measure = useCallback(() => {
    if (!run) return;
    const stateNow = readCoachState();
    if (!sameCoachState(stateNow, coachStateRef.current)) {
      coachStateRef.current = stateNow;
      setCoachState(stateNow);
    }
    if (!step) return;

    const el = document.querySelector(`[data-coach="${step.anchor}"]`) as HTMLElement | null;
    const stepKey = `${run.kind}:${run.tourId}:${run.index}`;

    if (el !== anchorElRef.current) {
      anchorResizeObserverRef.current?.disconnect();
      anchorResizeObserverRef.current = null;
      anchorElRef.current = el;
      if (el) {
        // A size change on the anchor usually means its container is
        // animating too (the sidebar collapse swaps the avatar button to its
        // compact form at the START of a 200ms width transition, and nothing
        // else fires when that transition lands). Track for a while, not once.
        const ro = new ResizeObserver(() => settle(450));
        ro.observe(el);
        anchorResizeObserverRef.current = ro;
      }
      scrolledForStepRef.current = null;
    }

    // The advance listener follows both the step and the element. An
    // `event:` advance needs no element at all, so this also runs when two
    // consecutive steps are both unanchored (el === el === null).
    if (el !== anchorElRef.current || stepKey !== advanceStepKeyRef.current || advanceCleanupRef.current === null) {
      advanceCleanupRef.current?.();
      advanceCleanupRef.current = attachAdvanceListener(el, step.advance, () => onAdvanceRef.current());
      advanceStepKeyRef.current = stepKey;
    }

    const viewport: CoachSize = { width: window.innerWidth, height: window.innerHeight };
    const rect = el ? el.getBoundingClientRect() : null;
    const zeroArea = !rect || rect.width === 0 || rect.height === 0;
    const fullyOutside = !rect || rect.right <= 0 || rect.bottom <= 0 || rect.left >= viewport.width || rect.top >= viewport.height;
    const resolvesNow = !!el && !zeroArea && !fullyOutside;

    // An optional card only exists while its target does. Reached through an
    // interaction (the previous card's advance), give the target a moment to
    // mount; otherwise, or once the grace is over, skip in the direction of
    // travel. Never a centred card: "appear if present" is the whole point.
    if (step.optional && !resolvesNow) {
      const grace = enteredViaAdvanceRef.current ? OPTIONAL_REVEAL_GRACE_MS : 0;
      const elapsed = performance.now() - enteredAtRef.current;
      if (elapsed < grace) {
        setWaiting(true);
        if (waitSettleForRef.current !== stepKey) {
          waitSettleForRef.current = stepKey;
          settle(grace + 120);
        }
        return;
      }
      if (skipIssuedForRef.current !== stepKey) {
        skipIssuedForRef.current = stepKey;
        setWaiting(false);
        const slotsNow = slotsOf(run.steps);
        if (dirRef.current === -1 && run.index > 0) back();
        else if (run.index >= slotsNow.length - 1) end('completed');
        else { dirRef.current = 1; next(); }
      }
      return;
    }
    setWaiting(false);

    if (!el || zeroArea || fullyOutside || !rect) {
      setCentered(true);
      setPlaced(null);
      setRing(null);
      return;
    }

    // A section-sized anchor (the templates grid, a whole pane) must never
    // drag the page around to fit itself — that yanks the header off-screen
    // and leaves the card pinned to a corner. Only scroll it when its top edge
    // is below the fold, and then just enough to bring that edge up; place the
    // card against the part that is actually visible.
    const tall = isTallAnchor(rect, viewport);
    const partiallyOutside = rect.top < 0 || rect.left < 0 || rect.bottom > viewport.height || rect.right > viewport.width;
    const shouldScroll = tall ? rect.top > viewport.height * 0.8 : partiallyOutside;
    if (shouldScroll && scrolledForStepRef.current !== step.anchor) {
      scrolledForStepRef.current = step.anchor;
      try {
        el.scrollIntoView({ block: tall ? 'start' : 'nearest', inline: 'nearest', behavior: reducedMotion ? 'auto' : 'smooth' });
      } catch {
        /* older browsers without smooth-scroll support */
      }
      settle(300);
    }

    setCentered(false);
    const placeRect = tall ? clipRectToViewport(rect, viewport) : rect;
    const anchorRect: CoachRect = { top: placeRect.top, left: placeRect.left, width: placeRect.width, height: placeRect.height };
    setPlaced(placeCard(anchorRect, cardSize, viewport, step.side));

    if (step.highlight === 'none') {
      setRing(null);
    } else {
      // Ring sits 6px outside the anchor, so its radius has to grow by the
      // same 6px to stay concentric — reusing the anchor's radius verbatim
      // draws visibly tighter corners than the thing it's tracing. Only the
      // simple single-length case is adjustable; anything exotic (percentages,
      // per-corner shorthand) is passed through as-is rather than mis-parsed.
      const computed = window.getComputedStyle(el);
      const raw = computed.borderRadius || '8px';
      const simplePx = /^([\d.]+)px$/.exec(raw.trim());
      setRing({
        top: rect.top - 6,
        left: rect.left - 6,
        width: rect.width + 12,
        height: rect.height + 12,
        radius: simplePx ? `${parseFloat(simplePx[1]) + 6}px` : raw,
      });
    }
  }, [run, step, cardSize, reducedMotion, schedule, settle, next, back, end]);

  measureRef.current = measure;

  // Clean up when the run ends.
  useEffect(() => {
    if (run) return;
    anchorResizeObserverRef.current?.disconnect();
    anchorResizeObserverRef.current = null;
    anchorElRef.current = null;
    scrolledForStepRef.current = null;
    detachAdvance();
    if (settleRafRef.current != null) {
      cancelAnimationFrame(settleRafRef.current);
      settleRafRef.current = null;
    }
    setPlaced(null);
    setRing(null);
    setCentered(false);
    setCardSize(DEFAULT_CARD_SIZE);
  }, [run, detachAdvance]);

  // Stop both loops on unmount — otherwise a settle window outlives the
  // component and keeps measuring against a torn-down tree.
  useEffect(() => () => {
    if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    if (settleRafRef.current != null) cancelAnimationFrame(settleRafRef.current);
    anchorResizeObserverRef.current?.disconnect();
    advanceCleanupRef.current?.();
  }, []);

  // Settle windows: longer on run start, shorter on every step change.
  useEffect(() => {
    if (!run) return;
    settle(600);
  }, [run?.kind, run?.tourId, settle]);

  // Layout effect (not passive) so it runs BEFORE the first-measure layout
  // effect below: measure() reads these refs to decide whether an optional
  // step is still inside its grace window.
  useLayoutEffect(() => {
    if (!run) return;
    // Only stamp when the transition was not requested through the Host's own
    // handlers (those stamp at request time, see goNext/onAdvance).
    if (enteredAtRef.current === 0 || !enteredViaAdvanceRef.current) {
      enteredAtRef.current = performance.now();
    }
    skipIssuedForRef.current = null;
    waitSettleForRef.current = null;
    setWaiting(false);
    settle(260);
  }, [run?.kind, run?.tourId, run?.index, settle]);

  // Global listeners: resize, scroll (capture — inner scroll containers),
  // a reflow catch-all on the document, and the repo's documented
  // cross-component idiom for the file-panel sidebar auto-collapse.
  useEffect(() => {
    const onResize = () => schedule();
    const onScroll = () => schedule();
    const onFilePanel = () => settle(300);
    // Any CSS transition/animation ending anywhere (sidebar width, panel
    // slide, drawer) may have moved the anchor without a DOM event of its own.
    const onMotionEnd = () => settle(200);
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('founding:filePanel', onFilePanel);
    window.addEventListener('transitionend', onMotionEnd, true);
    window.addEventListener('animationend', onMotionEnd, true);

    const docObserver = new ResizeObserver(() => schedule());
    docObserver.observe(document.documentElement);
    // The content column resizes whenever a sidebar changes width — the one
    // reliable signal for the "moving anchor" case.
    const main = document.querySelector('main');
    const mainObserver = main ? new ResizeObserver(() => settle(300)) : null;
    if (main && mainObserver) mainObserver.observe(main);

    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('founding:filePanel', onFilePanel);
      window.removeEventListener('transitionend', onMotionEnd, true);
      window.removeEventListener('animationend', onMotionEnd, true);
      docObserver.disconnect();
      mainObserver?.disconnect();
    };
  }, [schedule, settle]);

  // Escape — window capture so we fire before ui/Modal.tsx's document
  // capture listener can stopPropagation() it away. Early-return with no
  // run active; early-return again if a real modal (aria-modal="true") is
  // open — Escape belongs to the modal, not the tour, which renders
  // aria-modal="false" and so never matches this selector itself.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!run) return;
      if (e.key !== 'Escape') return;
      if (document.querySelector('[role="dialog"][aria-modal="true"]')) return;
      end('dismissed');
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [run, end]);

  // Measure the card's actual rendered size (title/body length varies per
  // step) so top/bottom placement math uses the real height, not a guess.
  useLayoutEffect(() => {
    if (!run) return;
    const el = cardRef.current;
    if (!el) return;
    const width = Math.round(el.offsetWidth) || DEFAULT_CARD_SIZE.width;
    const height = Math.round(el.offsetHeight) || DEFAULT_CARD_SIZE.height;
    if (width !== cardSize.width || height !== cardSize.height) {
      setCardSize({ width, height });
    }
  }, [run, step, placed, centered, cardSize]);

  // Measure SYNCHRONOUSLY, before paint — not via schedule()'s rAF. A card
  // rendered before its first measure has top/left undefined, and a
  // `position: fixed` box with auto offsets falls back to its static position
  // (last child of <body>), so an rAF-deferred first measure paints the card
  // in the wrong place for one frame and starts its 180ms fade there before
  // snapping to the anchor. A layout effect lands the position in the same
  // frame the card first appears.
  //
  // Not solved by hiding the card until positioned: `visibility: hidden` also
  // drops it out of the accessibility tree, which would make the
  // focus-on-open in CoachMarkCard silently no-op.
  useLayoutEffect(() => {
    if (!run) return;
    measureRef.current();
  }, [run?.kind, run?.tourId, run?.index, paused]);

  // Resuming after a modal: the card remounts, so give the anchors a settle
  // window the same way a step change does.
  useEffect(() => {
    if (!run || paused) return;
    settle(260);
  }, [paused, run, settle]);

  // A slot with no variant that fits the page state has nothing to say —
  // move on (or finish) rather than show a blank card. But only once the
  // state this render picked with is the page's: a run that opens on a
  // variant card (Settings' tabs, the build screen's preview) renders first
  // with the empty state the host mounted with, and measure()'s update of it
  // has not landed yet when this effect runs — skipping then lost the card.
  useEffect(() => {
    if (!run || step) return;
    const live = readCoachState();
    if (!sameCoachState(live, coachState)) {
      coachStateRef.current = live;
      setCoachState(live);
      return;
    }
    if (isLastStep) end('completed');
    else next();
  }, [run, step, coachState, isLastStep, end, next]);

  // A real modal on top of the run hides card + ring until it closes — unless
  // the card's own target is INSIDE that modal (the "Use this template" button),
  // in which case the card is exactly where it should be. The run itself (step
  // index, listeners) is untouched either way, so it resumes in place.
  const dialogEl = paused ? blockingDialogElement() : null;
  const anchorInsideDialog = !!(dialogEl && anchorElRef.current && dialogEl.contains(anchorElRef.current));
  const hiddenByDialog = paused && !anchorInsideDialog;
  if (!run || !step || waiting || hiddenByDialog) return null;

  const counted = countedSlots(slots).length;
  const number = countedNumber(slots, run.index);

  return createPortal(
    <>
      {ring && (
        <div
          aria-hidden="true"
          className="coach-mark-ring"
          style={{
            position: 'fixed',
            top: ring.top,
            left: ring.left,
            width: ring.width,
            height: ring.height,
            border: '2px solid #3264F8',
            borderRadius: ring.radius,
            zIndex: COACH_RING_Z,
            pointerEvents: 'none',
          }}
        />
      )}
      {/* Keyed per run: a nudge can replace a tour (or vice versa) without
          `run` ever passing through null, and the card's focus-on-open effect
          relies on remounting once per run. */}
      <CoachMarkCard
        key={`${run.kind}:${run.tourId}`}
        ref={cardRef}
        kind={run.kind}
        isNew={run.isNew}
        hint={step.hint ?? defaultHint(step.advance)}
        autoFocus={run.kind !== 'nudge'}
        title={step.title}
        body={step.body}
        stepNumber={number}
        totalSteps={counted}
        showCounter={!step.optional}
        isFirstStep={isFirstStep}
        isLastStep={isLastStep}
        onNext={goNext}
        onBack={goBack}
        onClose={() => end('dismissed')}
        centered={centered}
        top={placed?.top}
        left={placed?.left}
        placement={placed?.placement}
        arrowOffset={placed?.arrowOffset}
      />
    </>,
    document.body,
  );
};
